/**
 * CareNotesTimeline - Renders the care notes timeline for the monthly report.
 *
 * Displays a modern card-based timeline of all care notes grouped by date.
 * Each note card shows severity, time, type, content, attachment, and creator.
 *
 * Severity is indicated via a colored left border accent and small badge.
 *
 * Requirements: 16.1, 16.2, 16.3, 16.4, 16.5
 */

import React from 'react';
import type { TaggedNote, SeverityTag } from '../types';

export interface CareNotesTimelineProps {
  notes: TaggedNote[];
}

/** Color accent for each severity tag */
const SEVERITY_COLORS: Record<SeverityTag, string> = {
  'Information': '#9e9e9e',
  'Partial Intake': '#ff9800',
  'Refused': '#e91e63',
  'Clinical Attention': '#d32f2f',
  'Image Attached': '#1976d2',
};

/** Light background for each severity tag */
const SEVERITY_BG: Record<SeverityTag, string> = {
  'Information': '#fafafa',
  'Partial Intake': '#fff8e1',
  'Refused': '#fce4ec',
  'Clinical Attention': '#ffebee',
  'Image Attached': '#e3f2fd',
};

/** Icon for each severity tag */
const SEVERITY_ICONS: Record<SeverityTag, string> = {
  'Information': 'ℹ️',
  'Partial Intake': '⚠️',
  'Refused': '🚫',
  'Clinical Attention': '🔴',
  'Image Attached': '📷',
};

/**
 * Format dateKey to a readable label.
 */
function formatDateLabel(dateKey: string): string {
  const d = new Date(dateKey + 'T00:00:00');
  const day = d.getDate();
  const month = d.toLocaleString('en-US', { month: 'short' });
  const weekday = d.toLocaleString('en-US', { weekday: 'short' });
  return `${weekday}, ${month} ${day}`;
}

/**
 * Group notes by dateKey.
 */
function groupByDate(notes: TaggedNote[]): Map<string, TaggedNote[]> {
  const map = new Map<string, TaggedNote[]>();
  for (const n of notes) {
    const key = n.note.dateKey;
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(n);
  }
  return map;
}

/**
 * Capitalize note type label.
 */
function formatNoteType(noteType: string): string {
  if (!noteType) return '';
  return noteType.charAt(0).toUpperCase() + noteType.slice(1);
}

/**
 * Single note card component.
 */
const NoteCard: React.FC<{ taggedNote: TaggedNote }> = ({ taggedNote }) => {
  const { note, severityTag } = taggedNote;
  const accentColor = SEVERITY_COLORS[severityTag];
  const bgColor = SEVERITY_BG[severityTag];

  return (
    <div
      style={{
        display: 'flex',
        gap: '12px',
        padding: '12px 16px',
        backgroundColor: bgColor,
        borderLeft: `3px solid ${accentColor}`,
        borderRadius: '8px',
        marginBottom: '8px',
      }}
    >
      {/* Time column */}
      <div style={{ flexShrink: 0, minWidth: '52px' }}>
        <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#333' }}>
          {note.timeLabel}
        </div>
        <div style={{ fontSize: '0.7rem', color: '#999', marginTop: '2px', textTransform: 'capitalize' }}>
          {formatNoteType(note.noteType)}
        </div>
      </div>

      {/* Content column */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Severity badge */}
        <div style={{ marginBottom: '4px' }}>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '3px',
              padding: '2px 8px',
              borderRadius: '10px',
              fontSize: '0.68rem',
              fontWeight: 600,
              color: accentColor,
              backgroundColor: 'rgba(255,255,255,0.8)',
              border: `1px solid ${accentColor}30`,
            }}
          >
            {SEVERITY_ICONS[severityTag]} {severityTag}
          </span>
        </div>

        {/* Note text */}
        <p style={{ margin: '4px 0 0', fontSize: '0.84rem', color: '#333', lineHeight: 1.5, wordBreak: 'break-word' }}>
          {note.noteText}
        </p>

        {/* Attachment */}
        {(note.imageUrl || note.imageId) && (
          <div style={{ marginTop: '6px' }}>
            {note.imageUrl ? (
              <a
                href={note.imageUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '0.75rem',
                  color: '#1976d2',
                  textDecoration: 'none',
                  padding: '3px 8px',
                  backgroundColor: '#e3f2fd',
                  borderRadius: '6px',
                }}
              >
                📷 View Attachment
              </a>
            ) : (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '0.75rem',
                  color: '#1976d2',
                  padding: '3px 8px',
                  backgroundColor: '#e3f2fd',
                  borderRadius: '6px',
                }}
              >
                📷 Image #{note.imageId}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Creator column */}
      <div style={{ flexShrink: 0, textAlign: 'right' }}>
        <div style={{ fontSize: '0.75rem', color: '#777' }}>
          {note.creatorName}
        </div>
      </div>
    </div>
  );
};

const CareNotesTimeline: React.FC<CareNotesTimelineProps> = ({ notes }) => {
  if (notes.length === 0) {
    return (
      <div style={{
        padding: '2rem',
        textAlign: 'center',
        backgroundColor: '#fafafa',
        borderRadius: '12px',
      }}>
        <p style={{ color: '#999', fontStyle: 'italic', margin: 0 }}>
          No care notes available for this month.
        </p>
      </div>
    );
  }

  const grouped = groupByDate(notes);
  const sortedDates = Array.from(grouped.keys()).sort();

  return (
    <div className="care-notes-timeline" style={{ padding: '0.5rem 0' }}>
      {sortedDates.map((dateKey) => {
        const dayNotes = grouped.get(dateKey)!;
        return (
          <div key={dateKey} style={{ marginBottom: '1.25rem' }}>
            {/* Date header */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                marginBottom: '8px',
              }}
            >
              <div
                style={{
                  fontSize: '0.82rem',
                  fontWeight: 700,
                  color: '#444',
                  whiteSpace: 'nowrap',
                }}
              >
                {formatDateLabel(dateKey)}
              </div>
              <div style={{ flex: 1, height: '1px', backgroundColor: '#e8e8e8' }} />
              <div
                style={{
                  fontSize: '0.7rem',
                  color: '#999',
                  backgroundColor: '#f5f5f5',
                  padding: '2px 8px',
                  borderRadius: '10px',
                  fontWeight: 500,
                }}
              >
                {dayNotes.length} {dayNotes.length === 1 ? 'note' : 'notes'}
              </div>
            </div>

            {/* Notes for this date */}
            {dayNotes.map((taggedNote) => (
              <NoteCard key={`${taggedNote.note.id}-${taggedNote.note.noteType}`} taggedNote={taggedNote} />
            ))}
          </div>
        );
      })}
    </div>
  );
};

export default CareNotesTimeline;
